'use client';

import { useState, useEffect } from 'react';
import CustomImage from './Image';
import ImageModal from './ImageModal';
import { useRouter } from 'next/navigation';

const ImageCarousel = ({ images }) => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  
  // This ensures the component only runs interactive features on the client
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  // Auto-advance slides every 5 seconds when not hovered
  useEffect(() => {
    if (isHovered || images.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isHovered, images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToNextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Calculate aspect ratio (default to 16:9 if not specified)
  const aspectRatio = images[currentIndex]?.aspectRatio || '16/9';
  
  // If not on client yet, render a simpler version to avoid hydration errors
  if (!isClient) {
    return (
      <div className="relative w-full h-full z-10">
        <div 
          className="relative w-full h-full flex items-center justify-center" 
        >
          <div className="relative w-full h-full bg-dark-800 rounded-lg overflow-hidden">
            <CustomImage
              src={images[0].src}
              alt={images[0].alt}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              priority
              className="object-contain"
            />
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="relative w-full h-full z-10"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="relative w-full h-full flex items-center justify-center" 
      >
        {/* Main Image/GIF */}
        <div className="relative w-full h-full cursor-pointer" onClick={toggleModal}>
          <div className="relative w-full h-full group bg-dark-800 rounded-lg overflow-hidden">
            {images[currentIndex].src.toLowerCase().split('?')[0].endsWith('.gif') ? (
              <img
                src={images[currentIndex].src}
                alt={images[currentIndex].alt}
                className="absolute inset-0 w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <CustomImage
                src={images[currentIndex].src}
                alt={images[currentIndex].alt}
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                priority
                className="object-contain transition-transform duration-300 group-hover:scale-105"
              />
            )}
            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <span className="bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                Click to view larger
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Arrows - Only show if more than one image */}
        {images.length > 1 && (
          <>
            <button 
              onClick={goToPrevSlide}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50 transition-colors z-10"
              aria-label="Previous slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
              onClick={goToNextSlide}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white p-2 rounded-full hover:bg-black/50 transition-colors z-10"
              aria-label="Next slide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Dots Indicator - Only show if more than one image */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2 z-10">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-white w-6' : 'bg-white/50 w-2'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Image Modal */}
      <ImageModal
        isOpen={isModalOpen}
        onClose={toggleModal}
        imageUrl={images[currentIndex].src}
        alt={images[currentIndex].alt}
      />
    </div>
  );
};

export default ImageCarousel;
